/**
 * 55 — the home screen: THE RECORD.
 *
 * There is one album, and it's called 2026. This screen is that record, made
 * into something you can hold. The FRONT is the big disc on its platter and the
 * album name set enormous. The BACK is the tracklist — a numbered run you read
 * top to bottom, the way you read the back of a sleeve.
 *
 * On a phone it stays exactly that: the disc up top, the tracklist stacked
 * under it. On a wide screen it opens into two columns — a bigger record player
 * held sticky on the left while the tracklist scrolls on the right as a set of
 * tidy tile-cards.
 *
 * The gestures:
 *
 *   TAP A LINE  → opens the song (its versions, its takes, its notes).
 *   TAP THE DISC → drops the needle on track one, or pauses if it's already on.
 *   TAP A LINE'S ▶ → plays just that track.
 *   TAP A LINE'S ⋯ → open / rename / take it off the record.
 *   DRAG A LINE (in record order) → resequence the record.
 *
 * Sort is three modes — last worked on (the default), A–Z, and record order —
 * and only record order carries the Side A / Side B flip and the drag handle,
 * because a sequence is the only thing an order means anything to.
 */

import {
  getAlbum,
  setAlbumOrder,
  deleteSong,
  patchSong,
  getRecent,
  confirmTake,
  scrapTake,
  createSong,
  getSongs,
  getTakeStream,
  ApiError,
  type Album,
  type AlbumTrack,
  type Song,
  type RecentTake,
} from './api';
import * as player from './player';
import { createVinyl, type Vinyl } from './vinyl';
import {
  h,
  clear,
  icon,
  fmtTime,
  fmtDate,
  relDate,
  plural,
  emptyState,
  toast,
  promptSheet,
  sheet,
  isLight,
  toggleTheme,
} from './ui';

export interface Screen {
  destroy(): void;
}

/**
 * The label ring colour. It is the band red — the only colour on a monochrome
 * screen — and it never codes for a person. NOT the old neon '#ff3333'.
 */
const LABEL = '#d8241a';

/** The drag handle. Six dots, mono, no glow — the grip on a road-case latch. */
const GRIP = `<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="6" r="1.5"/><circle cx="15" cy="6" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="18" r="1.5"/></svg>`;

/** Scrap a recording — a plain bin, stroked to match the other nav icons. */
const TRASH_SVG = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 7h16M9 7V5h6v2M7 7l1 12h8l1-12"/></svg>`;

/** Headphones — the quiet glyph on the listening indicator. Stroked to match. */
const HEADPHONES_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 13a8 8 0 0 1 16 0"/><rect x="3" y="13" width="4" height="7" rx="1.4"/><rect x="17" y="13" width="4" height="7" rx="1.4"/></svg>`;

/** Lyric glyphs for the tile: a quote mark for "has words", a padlock for
 *  "locked", and a little exclamation for "needs vetting". Stroked to match. */
const LYRIC_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 7h11M5 12h14M5 17h9"/></svg>`;
const LOCK_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>`;
const REVIEW_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l9 16H3z"/><path d="M12 10v4M12 17v.5"/></svg>`;

/**
 * The lyric-status glyph that rides with a track's metadata: nothing when a song
 * has no words yet, a quiet lyric mark when it does, a padlock when the master is
 * locked, and a red "needs vetting" flag when a take diverges from the locked
 * words and Brad hasn't ruled on it. Returns null for 'none'/absent so a song
 * without lyrics stays clean.
 */
function lyricGlyph(track: AlbumTrack): HTMLElement | null {
  const s = track.lyric_status;
  if (!s || s === 'none') return null;
  const map = {
    has: { svg: LYRIC_SVG, label: 'Has lyrics' },
    locked: { svg: LOCK_SVG, label: 'Lyrics locked' },
    review: { svg: REVIEW_SVG, label: 'Lyrics need vetting' },
  } as const;
  const it = map[s];
  return h('span', {
    class: 'lyric-line',
    'data-state': s,
    title: it.label,
    'aria-label': it.label,
    html: it.svg,
  });
}

/**
 * The listening indicator that rides with a track's metadata: a tiny headphones
 * glyph and "3 plays · 3 heard" — or "not heard yet" when nobody has. Built only
 * from play_count + listener_count, monochrome and small, so it sits under the
 * title without ever competing with it. Returns null when the Worker sent no
 * tallies at all (both undefined), so an old payload just shows nothing.
 */
function listeningWidget(track: AlbumTrack): HTMLElement | null {
  const plays = track.play_count;
  const heard = track.listener_count;
  if (plays == null && heard == null) return null; // no data — say nothing

  const nPlays = plays ?? 0;
  const nHeard = heard ?? 0;
  const heardYet = nHeard > 0 || nPlays > 0;

  const text = heardYet ? `${plural(nPlays, 'play')} · ${nHeard} heard` : 'not heard yet';

  return h('span', { class: 'listen-line', 'data-heard': heardYet ? '1' : '0' }, [
    h('span', { class: 'listen-ic', html: HEADPHONES_SVG }),
    h('span', { class: 'listen-txt', text }),
  ]);
}

type SortMode = 'record' | 'az' | 'worked';

interface State {
  album: Album | null;
  tracks: AlbumTrack[] | null;
  sort: SortMode;
  error: string | null;
  /** Just-recorded clips that still need filing. null = not fetched yet. */
  recent: RecentTake[] | null;
}

export function mountShelf(host: HTMLElement, onOpenSong: (id: string) => void): Screen {
  const state: State = { album: null, tracks: null, sort: 'worked', error: null, recent: null };

  const abort = new AbortController();
  const body = h('div', { class: 'alb' });

  /* Per-row handles, so playback state is PATCHED onto the existing rows rather
     than re-rendered. A full re-render mid-play would tear the hero disc down
     and rebuild it, and you would see it stutter. */
  const rows = new Map<string, { root: HTMLElement; play: HTMLElement }>();
  let heroVinyl: Vinyl | null = null;

  // The left column (hero + sort control) and the tracklist are rebuilt on
  // their own schedules: sorting or reordering must never restart the disc, so
  // they touch only the tracklist, never the hero.
  let leftEl: HTMLElement | null = null;
  let sortEl: HTMLElement | null = null;
  // The repeat toggle (off → one → all). Lives in the left column under the sort
  // control; re-created on every full render, so it's tracked for reset like the
  // others and repainted from player.getLoop() whenever the player state moves.
  let loopEl: HTMLButtonElement | null = null;
  let tracksEl: HTMLElement | null = null;
  // The "just recorded" strip. A sibling of the columns (never inside the sticky
  // left one), inserted just above the tracklist.
  let recentEl: HTMLElement | null = null;

  // Marks the shelf host so the desktop two-column width only ever applies to
  // this screen — every other screen keeps the narrow reading column.
  host.classList.add('alb-screen');

  /* ------------------------------------------------------------- chrome */

  // The one bit of chrome that survives: a quiet light/dark toggle. Everything
  // else on this screen is the record.
  const themeBtn = h('button', {
    class: 'iconbtn alb-theme',
    html: isLight() ? icon.moon : icon.sun,
    'aria-label': 'Light or dark',
  });
  themeBtn.addEventListener('click', () => {
    toggleTheme();
    themeBtn.innerHTML = isLight() ? icon.moon : icon.sun;
  });

  const offlineStrip = h('div', { class: 'offline-strip', hidden: true }, [
    h('span', { html: icon.cloud }),
    h('span', { text: "You're offline. This is the last thing you saw." }),
  ]);

  // The DAW is a SEPARATE app on its own hostname (daw.thedevilssons.ca), not an
  // in-app screen. This is the one clearly-external door out of the record and
  // into the workstation — a real anchor, new tab, so the record and whatever's
  // playing stay put. It deliberately does NOT live in the bottom nav, which is
  // for in-app screens only.
  const dawLink = h(
    'a',
    {
      class: 'alb-daw',
      href: 'https://daw.thedevilssons.ca',
      target: '_blank',
      rel: 'noopener noreferrer',
    },
    [h('span', { text: 'the workstation' }), h('span', { 'aria-hidden': 'true', text: '→' })],
  );

  host.append(h('div', { class: 'alb-top' }, [dawLink, themeBtn]), offlineStrip, body);

  /* -------------------------------------------------------------- render */

  function render(): void {
    // The old hero disc is being torn down; take its listeners with it.
    heroVinyl?.destroy();
    heroVinyl = null;
    closeRowMenu();
    rows.clear();
    clear(body);
    leftEl = sortEl = tracksEl = recentEl = null;
    loopEl = null;

    if (state.error) {
      const retry = h('button', { class: 'btn-sm primary', text: 'Try again' });
      retry.addEventListener('click', () => void load());
      body.appendChild(emptyState("Couldn't reach the vault", state.error, retry));
      return;
    }

    // The sleeve front paints even while the tracks are still landing — the
    // album is always 2026, so there is no reason to make the disc wait on a
    // round trip. Only the tracklist below it needs the network.
    leftEl = h('div', { class: 'alb-left' });
    leftEl.appendChild(hero());
    body.appendChild(leftEl);

    if (state.tracks === null) {
      body.appendChild(loadingList());
      return;
    }

    if (state.tracks.length === 0) {
      body.appendChild(
        emptyState(
          'Nothing on the record yet',
          "The 2026 sessions haven't been sorted into tracks. When a take gets saved as a version, it lands here.",
        ),
      );
      return;
    }

    // The sort control lives with the record player: in the left column on a
    // wide screen, and — because the left column is simply stacked on top on a
    // phone — as a compact row directly above the tracklist there.
    sortEl = sortControls();
    leftEl.appendChild(sortEl);
    // The repeat toggle sits right under the sort control — same width, same
    // place in both layouts (stacked on a phone, in the left column on desktop).
    leftEl.appendChild(loopControl());

    renderTracks(true);
    // Re-attach the just-recorded strip if we already have clips in hand (a full
    // re-render, e.g. a retry, rebuilds the body from scratch). On first paint
    // there's nothing yet; loadRecent() fills it in a moment later.
    renderRecent();
  }

  /**
   * Rebuild ONLY the tracklist — never the hero. Sorting, dragging and removing
   * all come through here, so the disc keeps spinning through every one of them.
   */
  function renderTracks(animate = false): void {
    if (!state.tracks) return;
    const fresh = buildTracklist(animate);
    if (tracksEl && tracksEl.parentElement) tracksEl.replaceWith(fresh);
    else body.appendChild(fresh);
    tracksEl = fresh;

    if (sortEl) {
      for (const b of sortEl.querySelectorAll<HTMLElement>('.sort-opt')) {
        b.setAttribute('aria-pressed', b.dataset.mode === state.sort ? 'true' : 'false');
      }
    }
    paintPlaying();
  }

  /* ------------------------------------------------------- the sleeve front */

  function hero(): HTMLElement {
    const album = state.album;
    const title = album?.title ?? '2026';
    const subtitle = album?.subtitle ?? 'the writing sessions';
    const count = album?.track_count ?? state.tracks?.length ?? null;
    const year = album?.year ?? 2026;

    heroVinyl = createVinyl({ color: LABEL, arm: true });

    const disc = h('button', {
      class: 'alb-disc',
      'aria-label': `Play ${title} from the top`,
    });
    disc.appendChild(heroVinyl.el);
    disc.addEventListener('click', () => void playAlbum());

    return h('div', { class: 'alb-hero' }, [
      h('div', { class: 'alb-platter' }, [disc]),
      h('h1', { class: 'alb-title', text: title }),
      h('div', { class: 'alb-sub', text: subtitle }),
      h('div', {
        class: 'alb-meta',
        text: count != null ? `${count} tracks · ${year}` : String(year),
      }),
    ]);
  }

  /* ---------------------------------------------------------- the sort control */

  function sortControls(): HTMLElement {
    const wrap = h('div', { class: 'sort', role: 'group', 'aria-label': 'Sort the record' });
    const opts: [SortMode, string][] = [
      ['worked', 'Last worked on'],
      ['az', 'A–Z'],
      ['record', 'Record order'],
    ];
    for (const [mode, label] of opts) {
      const b = h('button', {
        class: 'sort-opt',
        'data-mode': mode,
        'aria-pressed': state.sort === mode ? 'true' : 'false',
        text: label,
      });
      b.addEventListener('click', () => setSort(mode));
      wrap.appendChild(b);
    }
    return wrap;
  }

  function setSort(mode: SortMode): void {
    if (state.sort === mode) return;
    state.sort = mode;
    closeRowMenu();
    renderTracks(false);
  }

  /* --------------------------------------------------------- the repeat toggle */

  /**
   * One button, cycling off → one → all → off. Plain words under a repeat glyph,
   * because "↻" alone means nothing to someone who isn't looking for it:
   *   off = "No repeat", one = "Repeat one", all = "Repeat all".
   * It only sets the mode (player.setLoop); the player itself decides what to do
   * when a track ends. The initial state comes straight from player.getLoop(),
   * which is persisted, so the record remembers how you left it.
   */
  const LOOP_LABEL: Record<player.LoopMode, string> = {
    off: 'No repeat',
    one: 'Repeat one',
    all: 'Repeat all',
  };
  const LOOP_NEXT: Record<player.LoopMode, player.LoopMode> = {
    off: 'one',
    one: 'all',
    all: 'off',
  };

  function loopControl(): HTMLElement {
    const btn = h('button', { class: 'loop-btn', type: 'button' }) as HTMLButtonElement;
    loopEl = btn;
    btn.addEventListener('click', () => {
      player.setLoop(LOOP_NEXT[player.getLoop()]);
      paintLoop();
    });
    paintLoop();
    return btn;
  }

  function paintLoop(): void {
    const btn = loopEl;
    if (!btn) return;
    const mode = player.getLoop();
    btn.dataset.mode = mode;
    // "pressed" = repeat is doing something; off is the resting state.
    btn.setAttribute('aria-pressed', mode === 'off' ? 'false' : 'true');
    btn.setAttribute('aria-label', `Repeat: ${LOOP_LABEL[mode]}`);
    clear(btn);
    btn.append(
      h('span', { class: 'loop-glyph', 'aria-hidden': 'true', text: '↻' }),
      h('span', { class: 'loop-label', text: LOOP_LABEL[mode] }),
    );
    // The one/all distinction as a tiny mono badge on the glyph, for people who
    // do read the icon — "1" for repeat-one, "∞" for repeat-all, nothing for off.
    if (mode !== 'off') {
      btn.querySelector('.loop-glyph')?.appendChild(
        h('span', { class: 'loop-badge', 'aria-hidden': 'true', text: mode === 'one' ? '1' : '∞' }),
      );
    }
  }

  /** Record order is the array itself; the other two are display-only copies —
   *  they never disturb the sequence the record is actually stored in. */
  function sortedTracks(): AlbumTrack[] {
    const t = state.tracks ?? [];
    if (state.sort === 'az') {
      return [...t].sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));
    }
    if (state.sort === 'worked') {
      return [...t].sort((a, b) => (b.latest_take_at ?? 0) - (a.latest_take_at ?? 0));
    }
    return t;
  }

  /* -------------------------------------------------------- the sleeve back */

  function buildTracklist(animate: boolean): HTMLElement {
    const ol = h('ol', { class: 'alb-tracks', 'data-sort': state.sort });
    rows.clear();

    const list = sortedTracks();

    if (state.sort !== 'record') {
      list.forEach((t, i) => ol.appendChild(trackRow(t, i, animate)));
      return ol;
    }

    // Record order carries the record itself: Side A, the flip to Side B, and a
    // quiet warning if it simply won't fit on one disc.
    const sideSec = (state.album?.side_minutes ?? 20) * 60;
    const dur = (t: AlbumTrack): number => t.canonical?.duration_s ?? 0;

    // Walk the order, accumulating time; the first track that would push Side A
    // past its length opens Side B. Only ever one flip.
    let flipIndex = -1;
    let running = 0;
    for (let i = 0; i < list.length; i++) {
      const d = dur(list[i]!);
      if (flipIndex < 0 && i > 0 && running + d > sideSec) {
        flipIndex = i;
        running = 0;
      }
      running += d;
    }

    let sideA = 0;
    let sideB = 0;
    list.forEach((t, i) => {
      if (flipIndex < 0 || i < flipIndex) sideA += dur(t);
      else sideB += dur(t);
    });

    ol.appendChild(sideDivider('A', sideA));
    list.forEach((t, i) => {
      if (i === flipIndex) ol.appendChild(sideDivider('B', sideB));
      ol.appendChild(trackRow(t, i, animate));
    });

    if (sideA + sideB > 2 * sideSec) {
      ol.appendChild(
        h('li', {
          class: 'side-warn',
          text: "That's more than fits on one record — trim the order or raise the side length in Settings.",
        }),
      );
    }

    return ol;
  }

  function sideDivider(letter: 'A' | 'B', seconds: number): HTMLElement {
    return h('li', { class: 'side-div', role: 'separator', 'aria-label': `Side ${letter}` }, [
      h('span', { class: 'side-rule', 'aria-hidden': 'true' }),
      h('span', { class: 'side-label', text: `SIDE ${letter} · ${fmtTime(seconds)}` }),
      h('span', { class: 'side-rule', 'aria-hidden': 'true' }),
    ]);
  }

  function trackRow(track: AlbumTrack, i: number, animate: boolean): HTMLElement {
    const no = track.track_no ?? i + 1;
    const canon = track.canonical;
    const isRecord = state.sort === 'record';

    const grip = h('span', { class: 'alb-grip', 'aria-hidden': 'true', html: GRIP });
    const tnum = h('span', { class: 'alb-tnum', text: String(no).padStart(2, '0') });

    const tline = h('span', { class: 'alb-tline' }, [
      h('span', { class: 'alb-ttitle', text: track.title }),
    ]);
    // The unheard flag is the WORD, in mono — not a coloured dot.
    if (track.unheard) tline.appendChild(h('span', { class: 'alb-new', text: 'NEW' }));

    // The take count rides under the title on the wide tile-cards; on a phone
    // row there's no room, so it's hidden there (see the desktop CSS).
    const takes = h('span', { class: 'alb-takes', text: plural(track.take_count, 'take') });
    // The quiet listening indicator sits with the metadata under the title — on
    // a phone it's the only sub-line, on a wide card it joins the take count.
    const listen = listeningWidget(track);
    // A small lyric-status glyph beside the listening line: has-words / locked /
    // needs-vetting. Absent when the song has no words yet.
    const lyric = lyricGlyph(track);
    const titleWrap = h('span', { class: 'alb-twrap' }, [tline, takes, listen, lyric]);

    const timeCell = h('div', { class: 'alb-ttime' });
    if (canon && canon.duration_s) {
      timeCell.appendChild(h('span', { class: 'alb-tdur', text: fmtTime(canon.duration_s) }));
    } else if (canon) {
      // A version exists but its length isn't known yet.
      timeCell.appendChild(h('span', { class: 'alb-tdur', text: '—' }));
    } else {
      // No canonical version: an em dash, and a quiet nudge to go pick one.
      timeCell.appendChild(h('span', { class: 'alb-tdur alb-tdur--none', text: '—' }));
      timeCell.appendChild(h('span', { class: 'alb-pick', text: 'pick one' }));
    }

    const play = h('button', {
      class: canon ? 'alb-play' : 'alb-play alb-play--ghost',
      'aria-label': canon ? `Play ${track.title}` : `Open ${track.title}`,
    });
    paintPlayBtn(play, track, false);

    const menu = h('button', {
      class: 'alb-menu',
      'aria-label': `More for ${track.title}`,
      'aria-haspopup': 'menu',
      html: icon.more,
    });

    const actions = h('div', { class: 'alb-actions' }, [play, menu]);

    const root = h(
      'li',
      {
        class: 'alb-track',
        'data-song': track.id,
        // Staggered drop, only on the first paint. Capped so a long record
        // doesn't leave the last line waiting half a second to arrive — and off
        // entirely on re-sort/re-order, where a re-run would read as a glitch.
        style: animate ? `animation-delay:${Math.min(i, 12) * 40}ms` : 'animation:none',
      },
      [grip, tnum, titleWrap, timeCell, actions],
    );
    root.draggable = isRecord;

    // Tap the line → open the song. The controls (play, ⋯, the drag grip) and an
    // in-progress rename all swallow the tap so it doesn't reach the row.
    root.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('.alb-play,.alb-menu,.alb-grip,.alb-rename')) return;
      onOpenSong(track.id);
    });
    play.addEventListener('click', (e) => {
      e.stopPropagation();
      void playTrack(track);
    });
    menu.addEventListener('click', (e) => {
      e.stopPropagation();
      toggleRowMenu(menu, track);
    });

    // Drag-to-reorder — record order only. The handlers no-op in the other
    // modes, and the grip shows dimmed there so it reads as "not now".
    root.addEventListener('dragstart', (e) => onDragStart(e, track.id, root));
    root.addEventListener('dragover', (e) => onDragOver(e, track.id, root));
    root.addEventListener('dragleave', () => clearDropMark(root));
    root.addEventListener('drop', (e) => onDrop(e, track.id, root));
    root.addEventListener('dragend', onDragEnd);

    rows.set(track.id, { root, play });
    return root;
  }

  /** The row's play control is icon-only — on a sleeve back the number and the
   *  title carry the weight, not a slab of a button. */
  function paintPlayBtn(btn: HTMLElement, _track: AlbumTrack, playing: boolean): void {
    btn.innerHTML = playing ? icon.pause : icon.play;
  }

  /* ----------------------------------------------------------- drag reorder */

  let dragId: string | null = null;

  function clearDropMark(row: HTMLElement): void {
    row.classList.remove('alb-drop-before', 'alb-drop-after');
  }

  function onDragStart(e: DragEvent, id: string, row: HTMLElement): void {
    if (state.sort !== 'record') {
      e.preventDefault();
      return;
    }
    dragId = id;
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      // Firefox refuses to start a drag unless some data is set.
      try {
        e.dataTransfer.setData('text/plain', id);
      } catch {
        /* some browsers throw here; the drag still works */
      }
    }
    row.classList.add('alb-track--dragging');
    closeRowMenu();
  }

  function onDragOver(e: DragEvent, overId: string, row: HTMLElement): void {
    if (!dragId || state.sort !== 'record' || overId === dragId) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    const r = row.getBoundingClientRect();
    const after = e.clientY > r.top + r.height / 2;
    row.classList.toggle('alb-drop-after', after);
    row.classList.toggle('alb-drop-before', !after);
  }

  function onDrop(e: DragEvent, overId: string, row: HTMLElement): void {
    if (!dragId || state.sort !== 'record') return;
    e.preventDefault();
    const after = row.classList.contains('alb-drop-after');
    clearDropMark(row);
    const from = dragId;
    dragId = null;
    if (from && from !== overId) void applyReorder(from, overId, after);
  }

  function onDragEnd(): void {
    dragId = null;
    for (const el of body.querySelectorAll<HTMLElement>(
      '.alb-track--dragging, .alb-drop-before, .alb-drop-after',
    )) {
      el.classList.remove('alb-track--dragging', 'alb-drop-before', 'alb-drop-after');
    }
  }

  /** Move `fromId` to sit before/after `toId`, renumber, paint it immediately,
   *  then make it stick. If the vault refuses, the order snaps back. */
  async function applyReorder(fromId: string, toId: string, after: boolean): Promise<void> {
    const tracks = state.tracks;
    if (!tracks) return;

    // Snapshot enough to put it all back on failure — order AND track numbers.
    const beforeOrder = tracks.map((t) => t.id);
    const beforeNos = new Map(tracks.map((t) => [t.id, t.track_no]));

    const fromIdx = tracks.findIndex((t) => t.id === fromId);
    if (fromIdx < 0) return;
    const [moved] = tracks.splice(fromIdx, 1);
    let toIdx = tracks.findIndex((t) => t.id === toId);
    if (toIdx < 0 || !moved) {
      tracks.splice(fromIdx, 0, moved as AlbumTrack);
      return;
    }
    if (after) toIdx += 1;
    tracks.splice(toIdx, 0, moved);

    tracks.forEach((t, i) => (t.track_no = i + 1));
    renderTracks(false);

    try {
      await setAlbumOrder(tracks.map((t) => t.id));
    } catch (e) {
      const pos = new Map(beforeOrder.map((id, i) => [id, i]));
      tracks.sort((a, b) => (pos.get(a.id) ?? 0) - (pos.get(b.id) ?? 0));
      tracks.forEach((t) => (t.track_no = beforeNos.get(t.id) ?? null));
      renderTracks(false);
      toast(e instanceof ApiError ? e.human : "Couldn't save the new order.", true);
    }
  }

  /* --------------------------------------------------------- the ⋯ row menu */

  let openMenu: {
    el: HTMLElement;
    scrim: HTMLElement;
    anchor: HTMLElement;
    onKey: (e: KeyboardEvent) => void;
  } | null = null;

  function closeRowMenu(): void {
    if (!openMenu) return;
    openMenu.el.remove();
    openMenu.scrim.remove();
    document.removeEventListener('keydown', openMenu.onKey, true);
    window.removeEventListener('resize', closeRowMenu);
    window.removeEventListener('scroll', closeRowMenu, true);
    openMenu = null;
  }

  function toggleRowMenu(anchor: HTMLElement, track: AlbumTrack): void {
    // A second tap on the same ⋯ closes it, rather than flickering it shut and
    // straight back open.
    if (openMenu && openMenu.anchor === anchor) {
      closeRowMenu();
      return;
    }
    openRowMenu(anchor, track);
  }

  function openRowMenu(anchor: HTMLElement, track: AlbumTrack): void {
    closeRowMenu();
    const overlay = document.getElementById('overlay');
    if (!overlay) return;

    const scrim = h('div', { class: 'rowmenu-scrim' });
    scrim.addEventListener('click', closeRowMenu);
    const el = h('div', { class: 'rowmenu', role: 'menu' });

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeRowMenu();
      }
    };

    overlay.append(scrim, el);
    openMenu = { el, scrim, anchor, onKey };
    renderMenuList(el, track, anchor);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', closeRowMenu);
    window.addEventListener('scroll', closeRowMenu, true);
  }

  function renderMenuList(el: HTMLElement, track: AlbumTrack, anchor: HTMLElement): void {
    clear(el);

    const open = menuItem('Open', () => {
      closeRowMenu();
      onOpenSong(track.id);
    });
    const rename = menuItem('Rename', () => {
      closeRowMenu();
      startRename(track);
    });
    const remove = menuItem('Take off the record', () => renderMenuConfirm(el, track, anchor), true);

    el.append(open, rename, h('div', { class: 'rowmenu-sep', 'aria-hidden': 'true' }), remove);
    positionMenu(el, anchor);
    (el.querySelector('button') as HTMLElement | null)?.focus();
  }

  function renderMenuConfirm(el: HTMLElement, track: AlbumTrack, anchor: HTMLElement): void {
    clear(el);

    const cancel = h('button', { class: 'btn-sm', text: 'Cancel' });
    cancel.addEventListener('click', () => renderMenuList(el, track, anchor));
    const confirm = h('button', { class: 'btn-sm primary', text: 'Take off' });
    confirm.addEventListener('click', () => void doRemove(track));

    el.appendChild(
      h('div', { class: 'rowmenu-confirm' }, [
        h('p', { class: 'rc-title', text: 'Take it off the record?' }),
        h('p', { class: 'rc-note', text: 'Nothing is deleted — every take and note stays put.' }),
        h('div', { class: 'rowmenu-actions' }, [cancel, confirm]),
      ]),
    );
    positionMenu(el, anchor);
    confirm.focus();
  }

  function menuItem(label: string, onClick: () => void, danger = false): HTMLElement {
    const b = h('button', {
      class: 'rowmenu-item',
      role: 'menuitem',
      'data-danger': danger ? '1' : null,
      text: label,
    });
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick();
    });
    return b;
  }

  function positionMenu(el: HTMLElement, anchor: HTMLElement): void {
    const r = anchor.getBoundingClientRect();
    el.style.visibility = 'hidden';
    el.style.left = '0px';
    el.style.top = '0px';
    const mw = el.offsetWidth;
    const mh = el.offsetHeight;
    const pad = 8;

    // Right-aligned to the ⋯, dropping below it — flipped above if it would run
    // off the bottom, and clamped so it never leaves the viewport.
    let left = r.right - mw;
    if (left + mw > window.innerWidth - pad) left = window.innerWidth - pad - mw;
    if (left < pad) left = pad;

    let top = r.bottom + 6;
    if (top + mh > window.innerHeight - pad) top = r.top - mh - 6;
    if (top < pad) top = pad;

    el.style.left = `${Math.round(left)}px`;
    el.style.top = `${Math.round(top)}px`;
    el.style.visibility = 'visible';
  }

  /* ------------------------------------------------------------- rename (inline) */

  function startRename(track: AlbumTrack): void {
    const entry = rows.get(track.id);
    const titleEl = entry?.root.querySelector('.alb-ttitle') as HTMLElement | null;
    if (!titleEl) return;

    const input = h('input', {
      class: 'alb-rename',
      type: 'text',
      value: track.title,
      maxlength: 120,
      'aria-label': `Rename ${track.title}`,
    }) as HTMLInputElement;
    titleEl.replaceWith(input);

    let done = false;
    const restore = (title: string) => {
      const span = h('span', { class: 'alb-ttitle', text: title });
      input.replaceWith(span);
      return span;
    };

    const commit = async () => {
      if (done) return;
      done = true;
      const name = input.value.trim();
      if (!name || name === track.title) {
        restore(track.title);
        return;
      }
      const span = restore(name);
      const old = track.title;
      track.title = name;
      try {
        await patchSong(track.id, { title: name });
      } catch (e) {
        track.title = old;
        span.textContent = old;
        toast(e instanceof ApiError ? e.human : "Couldn't rename it. Try again?", true);
      }
    };
    const cancel = () => {
      if (done) return;
      done = true;
      restore(track.title);
    };

    input.addEventListener('click', (e) => e.stopPropagation());
    input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        void commit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
      }
    });
    input.addEventListener('blur', () => void commit());

    // A field that appears but never takes focus reads as broken.
    setTimeout(() => {
      input.focus();
      input.select();
    }, 30);
  }

  /* ----------------------------------------------------------- remove (shelve) */

  async function doRemove(track: AlbumTrack): Promise<void> {
    closeRowMenu();
    try {
      await deleteSong(track.id);
    } catch (e) {
      toast(e instanceof ApiError ? e.human : "Couldn't take it off. Try again?", true);
      return;
    }

    const row = rows.get(track.id)?.root;
    let finished = false;
    const finalize = () => {
      if (finished) return;
      finished = true;
      if (state.tracks) {
        state.tracks = state.tracks.filter((t) => t.id !== track.id);
        state.tracks.forEach((t, i) => (t.track_no = i + 1));
      }
      if (state.album && typeof state.album.track_count === 'number') {
        state.album.track_count = Math.max(0, state.album.track_count - 1);
      }
      renderTracks(false);
    };

    if (row) {
      row.classList.add('alb-track--removing');
      row.addEventListener('transitionend', finalize, { once: true });
      // Belt and braces: if the transition never fires (reduced motion, a
      // display change), don't leave the row stranded on screen.
      setTimeout(finalize, 400);
    } else {
      finalize();
    }
  }

  /* --------------------------------------------------------------- playing */

  /**
   * The play queue for continuous playback — the whole record, IN THE ORDER
   * IT'S SHOWN, so tapping any track carries on song-to-song from there. Tracks
   * with no canonical version yet are skipped: there's nothing to play, and
   * stopping to open a picker mid-album would break the flow. The parallel `ids`
   * array is the song-id at each queue position, so a caller can find the start
   * index for the track they tapped.
   */
  function buildQueue(): { items: player.QueueItem[]; ids: string[] } {
    const items: player.QueueItem[] = [];
    const ids: string[] = [];
    for (const t of sortedTracks()) {
      const canon = t.canonical;
      if (!canon) continue;
      items.push({
        songId: t.id,
        versionId: canon.version_id,
        title: t.title,
        subtitle: canon.name || fmtDate(canon.recorded_at),
        color: LABEL,
      });
      ids.push(t.id);
    }
    return { items, ids };
  }

  /**
   * Drop the needle on the record. If we're already somewhere on 2026 the disc
   * is a play/pause; otherwise it loads the whole album as a queue and starts at
   * the top (the first track that has a version to play), so it runs end to end.
   * If nothing on the record has a canonical version yet, we send them to the top
   * track to pick one — we never surface "there is nothing to play".
   */
  async function playAlbum(): Promise<void> {
    const tracks = state.tracks ?? [];
    const now = player.get().now;

    if (now && tracks.some((t) => t.id === now.songId)) {
      player.toggle();
      return;
    }

    const { items } = buildQueue();
    if (items.length > 0) {
      await player.setQueue(items, 0);
      const err = player.get().error;
      if (err) toast(err, true);
      return;
    }
    if (tracks[0]) onOpenSong(tracks[0].id);
  }

  async function playTrack(track: AlbumTrack): Promise<void> {
    const canon = track.canonical;
    // Nothing agreed on yet — open the song so they can pick one. Not an error.
    if (!canon) {
      onOpenSong(track.id);
      return;
    }

    // Already the current track → just play/pause it. Don't rebuild the queue
    // out from under a track that's mid-play.
    if (player.isCurrent(canon.version_id)) {
      player.toggle();
      return;
    }

    // Continuous play: load the whole record (in the shown order) and start on
    // this track, so it carries on to the next song instead of stopping here.
    const { items, ids } = buildQueue();
    const index = ids.indexOf(track.id);
    if (index >= 0) {
      await player.setQueue(items, index);
    } else {
      // Belt and braces — a canonical track should always be in the queue, but
      // if somehow it isn't, still play the one track rather than nothing.
      await player.play({
        versionId: canon.version_id,
        songId: track.id,
        title: track.title,
        subtitle: canon.name || fmtDate(canon.recorded_at),
        color: LABEL,
      });
    }

    const err = player.get().error;
    if (err) toast(err, true);

    // They've heard it. The NEW flag has done its job.
    track.unheard = false;
    rows.get(track.id)?.root.querySelector('.alb-new')?.remove();
  }

  /**
   * Playback state is patched onto the disc and the rows, never re-rendered.
   * The hero disc spins whenever anything on this record is playing.
   */
  function paintPlaying(): void {
    const now = player.get().now;
    const playing = player.get().playing;
    const tracks = state.tracks ?? [];
    const onThisRecord = !!now && tracks.some((t) => t.id === now.songId);

    heroVinyl?.setPlaying(onThisRecord && playing);

    // Keep the repeat toggle honest with the player (it emits on setLoop too).
    paintLoop();

    for (const [songId, r] of rows) {
      const track = tracks.find((t) => t.id === songId);
      const isThis = !!now && now.songId === songId;
      r.root.dataset.current = isThis ? '1' : '0';
      if (track) paintPlayBtn(r.play, track, isThis && playing);
    }

    // Whenever the player's state moves — including an album track taking the
    // element over — the just-recorded ▶ buttons must agree.
    repaintPreview();
  }

  /* ========================================================= JUST RECORDED
   *
   * The fix for the thing that broke trust: a jam recorded on a phone used to
   * upload fine and then appear NOWHERE. Now it lands here, at the top of the
   * tracklist, the moment it's saved — one tap plays it, one more files it onto
   * a song. The strip draws ONLY when there are unfiled clips, and it never
   * blocks the record: if /api/recent falls over, the album carries on without
   * it.
   */

  // Playback BORROWS the app's one <audio> element — never a second one (two
  // media elements fight over the iOS audio session). It's the same element the
  // player already primed for iOS, so an awaited stream URL still plays.
  const audioEl = player.element();
  // The take id currently loaded into that element for a preview, or null.
  let previewId: string | null = null;
  // take_id → its ▶ button, so the icon flips without re-rendering the card.
  const previewBtns = new Map<string, HTMLElement>();

  const onAudio = () => repaintPreview();
  for (const ev of ['play', 'pause', 'ended', 'emptied'] as const) {
    audioEl.addEventListener(ev, onAudio);
  }

  // The song list for the picker, fetched once and kept warm so filing stays
  // instant (two taps: "which song?" → the song).
  let songsCache: Song[] | null = null;
  let songsPending: Promise<Song[]> | null = null;

  function prefetchSongs(): Promise<Song[]> {
    if (songsCache) return Promise.resolve(songsCache);
    if (songsPending) return songsPending;
    songsPending = getSongs(abort.signal)
      .then((s) => {
        songsCache = s;
        songsPending = null;
        return s;
      })
      .catch(() => {
        songsPending = null;
        return [];
      });
    return songsPending;
  }

  async function loadRecent(): Promise<void> {
    let takes: RecentTake[];
    try {
      takes = await getRecent(abort.signal);
    } catch {
      // Never block the album. No section is a fine outcome.
      return;
    }
    if (abort.signal.aborted) return;
    // Only clips with no song yet — the filed ones already live on the record.
    state.recent = takes.filter((t) => t.unfiled);
    renderRecent();
    if (state.recent.length > 0) void prefetchSongs();
  }

  function renderRecent(): void {
    recentEl?.remove();
    recentEl = null;
    previewBtns.clear();

    const items = state.recent;
    if (!items || items.length === 0) return;
    // Hang it right after the sleeve's left column, above the tracklist.
    if (!leftEl || !leftEl.parentElement) return;

    recentEl = buildRecent(items);
    leftEl.after(recentEl);
    repaintPreview();
  }

  function buildRecent(items: RecentTake[]): HTMLElement {
    return h('section', { class: 'alb-jr', 'aria-label': 'Just recorded' }, [
      h('div', { class: 'alb-jr-head' }, [
        h('span', { class: 'alb-jr-dot', 'aria-hidden': 'true' }),
        h('span', { text: 'Just recorded' }),
      ]),
      ...items.map(recentCard),
    ]);
  }

  function recentCard(take: RecentTake): HTMLElement {
    const play = h('button', {
      class: 'alb-jr-play',
      'aria-label': 'Play this clip',
      html: icon.play,
    });
    play.addEventListener('click', () => void playPreview(take));
    previewBtns.set(take.take_id, play);

    // "just now" leads; length and who recorded it ride under it.
    const detail: string[] = [];
    if (take.duration_s && take.duration_s > 0) detail.push(fmtTime(take.duration_s));
    if (take.by_name) detail.push(take.by_name);

    const meta = h('div', { class: 'alb-jr-meta' }, [
      h('span', { class: 'alb-jr-when', text: relTime(take.created_at) }),
      detail.length ? h('span', { class: 'alb-jr-detail', text: detail.join(' · ') }) : null,
    ]);

    // Scrap it — a bungled take is rubbish, and the band messes up sometimes.
    const scrap = h('button', {
      class: 'alb-jr-scrap',
      'aria-label': 'Scrap this recording',
      html: TRASH_SVG,
    });
    scrap.addEventListener('click', () => scrapRecording(take, scrap));

    const file = h('button', { class: 'alb-jr-file', text: 'Which song is this?' });
    file.addEventListener('click', () => void openPicker(take));

    return h('div', { class: 'alb-jr-card', 'data-take': take.take_id }, [
      h('div', { class: 'alb-jr-top' }, [play, meta, scrap]),
      file,
    ]);
  }

  /** Bin a recording. One confirm — it's gone and the bytes come back. */
  function scrapRecording(take: RecentTake, btn: HTMLElement): void {
    sheet('Scrap this recording?', [
      {
        label: "Scrap it — it's gone for good",
        accent: true,
        onSelect: () => {
          if (previewId === take.take_id) audioEl.pause();
          const card = btn.closest<HTMLElement>('.alb-jr-card');
          card?.classList.add('is-going');
          void scrapTake(take.take_id)
            .then(() => {
              if (state.recent) state.recent = state.recent.filter((t) => t.take_id !== take.take_id);
              window.setTimeout(() => renderRecent(), 180);
              toast('Scrapped.');
            })
            .catch((e) => {
              card?.classList.remove('is-going');
              toast(e instanceof ApiError ? e.human : "Couldn't scrap it. Try again?", true);
            });
        },
      },
      { label: 'Keep it', onSelect: () => undefined },
    ]);
  }

  /** "just now", "2 min ago", "3 hours ago" — then it hands off to day words. */
  function relTime(unix: number | null): string {
    if (!unix) return 'just now';
    const secs = Math.floor(Date.now() / 1000 - unix);
    if (secs < 45) return 'just now';
    if (secs < 90) return '1 min ago';
    if (secs < 3600) return `${Math.round(secs / 60)} min ago`;
    if (secs < 5400) return '1 hour ago';
    if (secs < 86400) return `${Math.round(secs / 3600)} hours ago`;
    return relDate(unix);
  }

  /* ------------------------------------------------- preview playback (borrowed) */

  async function playPreview(take: RecentTake): Promise<void> {
    const id = take.take_id;

    // Same clip we already own → pause if playing, resume if paused. No network.
    if (previewId === id && !player.get().now) {
      if (!audioEl.paused) {
        audioEl.pause();
        return;
      }
      if (audioEl.src && !audioEl.src.startsWith('data:')) {
        try {
          await audioEl.play();
        } catch {
          /* iOS may want a fresh tap; the icon just stays on play */
        }
        repaintPreview();
        return;
      }
    }

    // A new clip takes over the shared element. Stop whatever the player had.
    player.stop();
    previewId = id;
    repaintPreview();

    let url: string;
    try {
      url = (await getTakeStream(id, abort.signal)).url;
    } catch (e) {
      if (abort.signal.aborted) return;
      previewId = null;
      repaintPreview();
      toast(e instanceof ApiError ? e.human : "Couldn't play that one. Try again?", true);
      return;
    }
    if (previewId !== id || abort.signal.aborted) return; // they tapped elsewhere

    audioEl.src = url;
    audioEl.load();
    try {
      await audioEl.play();
    } catch (e) {
      if ((e as Error)?.name !== 'AbortError') toast("Couldn't start that one. Try again?", true);
    }
    repaintPreview();
  }

  function repaintPreview(): void {
    // If the player picked up a real track, the element is no longer ours.
    if (player.get().now) previewId = null;
    for (const [id, btn] of previewBtns) {
      const on = !player.get().now && previewId === id && !audioEl.paused;
      btn.innerHTML = on ? icon.pause : icon.play;
      btn.setAttribute('aria-label', on ? 'Pause this clip' : 'Play this clip');
    }
  }

  /* --------------------------------------------------- "which song is this?" */

  async function openPicker(take: RecentTake): Promise<void> {
    const overlay = document.getElementById('overlay');
    if (!overlay) return;

    const scrim = h('div', { class: 'scrim' });
    const panel = h('div', {
      class: 'alb-jr-sheet',
      role: 'dialog',
      'aria-label': 'Which song is this?',
    });

    let closed = false;
    const close = () => {
      if (closed) return;
      closed = true;
      scrim.remove();
      panel.remove();
      document.removeEventListener('keydown', onKey);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    scrim.addEventListener('click', close);
    document.addEventListener('keydown', onKey);

    // "New song" and "Not sure" stay pinned so both are always one tap away,
    // however long the song list gets; the songs themselves scroll between them.
    const newBtn = h('button', { class: 'alb-jr-pick alb-jr-pick--new' }, [
      h('span', { class: 'alb-jr-pick-plus', 'aria-hidden': 'true', text: '+' }),
      h('span', { text: 'New song' }),
    ]);
    newBtn.addEventListener('click', () => {
      close();
      void fileNew(take);
    });

    const listEl = h('div', { class: 'alb-jr-picklist' }, [
      h('div', { class: 'alb-jr-pickloading', text: 'Loading songs…' }),
    ]);

    const notSure = h('button', { class: 'alb-jr-notsure', text: 'Not sure yet' });
    notSure.addEventListener('click', close);

    panel.append(
      h('div', { class: 'grab' }),
      h('h3', { class: 'alb-jr-picktitle', text: 'Which song is this?' }),
      newBtn,
      listEl,
      notSure,
    );
    overlay.append(scrim, panel);

    const songs = await prefetchSongs();
    if (closed) return;
    clear(listEl);
    if (songs.length === 0) {
      listEl.append(
        h('div', { class: 'alb-jr-pickempty', text: 'No songs yet — make a new one above.' }),
      );
      return;
    }
    for (const s of songs) {
      const b = h('button', { class: 'alb-jr-pick', text: s.title });
      b.addEventListener('click', () => {
        close();
        void fileTake(take, s.id, s.title);
      });
      listEl.append(b);
    }
  }

  async function fileTake(take: RecentTake, songId: string, songTitle: string): Promise<void> {
    try {
      await confirmTake(take.take_id, songId);
    } catch (e) {
      toast(e instanceof ApiError ? e.human : "Couldn't file that. Try again?", true);
      return;
    }
    toast(`Filed under ${songTitle}`);
    removeRecent(take.take_id);
  }

  async function fileNew(take: RecentTake): Promise<void> {
    const title = await promptSheet({
      title: 'New song',
      label: 'What do you want to call it?',
      placeholder: 'Song name',
      confirm: 'Create',
    });
    if (!title) return;

    let created: { id: string; title: string };
    try {
      created = await createSong(title);
    } catch (e) {
      toast(e instanceof ApiError ? e.human : "Couldn't make that song. Try again?", true);
      return;
    }
    // A freshly made song — let the picker pick it up next time it opens.
    songsCache = null;

    try {
      await confirmTake(take.take_id, created.id);
    } catch (e) {
      toast(
        e instanceof ApiError
          ? e.human
          : `Made ${created.title}, but couldn't file the clip. Try again?`,
        true,
      );
      return;
    }
    toast(`Filed under ${created.title}`);
    removeRecent(take.take_id);
  }

  function removeRecent(takeId: string): void {
    if (state.recent) state.recent = state.recent.filter((t) => t.take_id !== takeId);
    // If that clip was previewing and nothing else grabbed the element, stop it.
    if (previewId === takeId && !player.get().now) {
      audioEl.pause();
      previewId = null;
    }
    renderRecent();
  }

  /* ----------------------------------------------------------- loading art */

  function loadingList(): HTMLElement {
    return h(
      'div',
      { class: 'alb-skel' },
      Array.from({ length: 8 }, () => h('div', { class: 'alb-skel-row' })),
    );
  }

  /* --------------------------------------------------------------- offline */

  function paintNet(): void {
    offlineStrip.hidden = navigator.onLine;
  }
  const onNet = () => paintNet();
  window.addEventListener('online', onNet);
  window.addEventListener('offline', onNet);

  /* ----------------------------------------------------- playback → the UI */

  const offState = player.subscribe(paintPlaying);
  const offTick = player.onTick((t, d) => {
    const now = player.get().now;
    if (now && (state.tracks ?? []).some((x) => x.id === now.songId)) {
      heroVinyl?.setProgress(t, d);
    }
  });

  /* ------------------------------------------------------------------ load */

  async function load(): Promise<void> {
    state.error = null;
    state.album = null;
    state.tracks = null;
    state.recent = null;
    render();
    try {
      const r = await getAlbum(abort.signal);
      state.album = r.album;
      state.tracks = r.tracks;
    } catch (e) {
      if (abort.signal.aborted) return;
      state.error = e instanceof ApiError ? e.human : 'Couldn’t reach the vault. Try again?';
    }
    render();
    // Separate, failure-isolated: the just-recorded strip never blocks the album.
    void loadRecent();
  }

  paintNet();
  void load();

  return {
    destroy() {
      abort.abort();
      closeRowMenu();
      offState();
      offTick();
      window.removeEventListener('online', onNet);
      window.removeEventListener('offline', onNet);
      // If a just-recorded clip is still previewing on the shared element and no
      // real track has taken over, stop it — leaving it playing with no visible
      // transport (the now-playing bar stays hidden for previews) would strand it.
      if (previewId && !player.get().now) audioEl.pause();
      for (const ev of ['play', 'pause', 'ended', 'emptied'] as const) {
        audioEl.removeEventListener(ev, onAudio);
      }
      host.classList.remove('alb-screen');
      heroVinyl?.destroy();
      rows.clear();
    },
  };
}
